import { useEffect, useState, useMemo, type CSSProperties } from "react";
import { getInventoryForPickList, type PickListBottle } from "../../lib/api";
import KioskControls from "../../components/common/KioskControls";
import BarcodeScannerModal from "../../components/common/BarcodeScannerModal";
import { SHELF_PICK_ORDER, parseShelfCode, shelfZoneLabel, type ParsedShelfCode } from "../../lib/shelfLocations";
import { useIsMobile } from "../../lib/useIsMobile";

const CACHE_KEY = "station.picklist_bottles_cache";
const CHECKED_KEY = "station.picklist_checked_state";

// Zone identity, walking order and labels all come from src/lib/shelfLocations.ts,
// the same table the shelf map and the Find bottle popup read. The copy that used
// to live here had drifted: it omitted LIB-O entirely, so every open-shelf bottle
// fell into "Unassigned", and its labels disagreed with the map the runner is
// holding.
interface PickListItem extends PickListBottle {
  _code: string;
  _loc: ParsedShelfCode | null;
}

const UNPLACED = { zoneIdx: SHELF_PICK_ORDER.length + 1, col: Number.MAX_SAFE_INTEGER, row: "Z" };

export default function AdminPickList() {
  const isPhone = useIsMobile(768);
  const [mode, setMode] = useState<"restock" | "unassigned" | "custom">("restock");
  const [parThreshold, setParThreshold] = useState<number>(2);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [bottles, setBottles] = useState<PickListBottle[]>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [customIds, setCustomIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOfflineCached, setIsOfflineCached] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>(() => {
    try {
      const cached = localStorage.getItem(CHECKED_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });

  // Custom Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getInventoryForPickList();
      if (data && Array.isArray(data)) {
        setBottles(data);
        setIsOfflineCached(false);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        } catch {}
      }
    } catch (e) {
      console.warn("Pick list offline / fetch failed. Using local cache:", e);
      setIsOfflineCached(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const toggleCheck = (idKey: string) => {
    setCheckedIds((prev) => {
      const next = { ...prev, [idKey]: !prev[idKey] };
      try {
        localStorage.setItem(CHECKED_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const clearAllChecks = () => {
    setCheckedIds({});
    try {
      localStorage.removeItem(CHECKED_KEY);
    } catch {}
  };

  const handleScanBarcode = (barcode: string) => {
    setScannerOpen(false);
    const clean = barcode.trim();
    const match = bottles.find(
      (b) => b.upc === clean || b.sku === clean || (b.upc && clean.includes(b.upc))
    );
    if (match) {
      const locs = match.shelf_location && match.shelf_location.length ? match.shelf_location : ["unassigned"];
      locs.forEach((loc) => {
        toggleCheck(`${match.id}_${loc}`);
      });
    }
  };

  const addCustomBottle = (b: PickListBottle) => {
    if (!customIds.includes(b.id)) {
      setCustomIds((prev) => [...prev, b.id]);
    }
    setSearchQuery("");
  };

  const removeCustomBottle = (id: string) => {
    setCustomIds((prev) => prev.filter((x) => x !== id));
  };

  // Custom search results dropdown
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return bottles.filter((b) => b.name.toLowerCase().includes(q)).slice(0, 10);
  }, [bottles, searchQuery]);

  // Mode items resolution with custom parThreshold support
  const itemsForMode: PickListBottle[] = useMemo(() => {
    if (mode === "restock") {
      return bottles.filter((b) => {
        const count = b.inventory_count != null ? b.inventory_count : 999;
        const threshold = b.reorder_point || parThreshold;
        return (
          count <= threshold &&
          b.shelf_location &&
          Array.isArray(b.shelf_location) &&
          b.shelf_location.length > 0
        );
      });
    } else if (mode === "unassigned") {
      return bottles.filter((b) => !b.shelf_location || !b.shelf_location.length);
    } else {
      return bottles.filter((b) => customIds.includes(b.id));
    }
  }, [bottles, mode, customIds, parThreshold]);

  // Flatten & sort items by physical shelf walking path
  const groupedZones = useMemo(() => {
    const locItems: PickListItem[] = [];

    itemsForMode.forEach((b) => {
      const locs = b.shelf_location || [];
      if (!locs.length) {
        locItems.push({ ...b, _code: "No Location", _loc: null });
      } else {
        locs.forEach((code) => {
          locItems.push({ ...b, _code: code, _loc: parseShelfCode(code) });
        });
      }
    });

    // Apply secondary in-page filter
    const fq = filterQuery.trim().toLowerCase();
    const filtered = fq
      ? locItems.filter(
          (item) =>
            item.name.toLowerCase().includes(fq) ||
            item._code.toLowerCase().includes(fq) ||
            (item.category_slug && item.category_slug.toLowerCase().includes(fq))
        )
      : locItems;

    // Sort walking order: Zone -> Column -> Row -> Name
    filtered.sort((a, b) => {
      const la = a._loc || UNPLACED;
      const lb = b._loc || UNPLACED;
      if (la.zoneIdx !== lb.zoneIdx) return la.zoneIdx - lb.zoneIdx;
      if (la.col !== lb.col) return la.col - lb.col;
      if (la.row !== lb.row) return la.row.localeCompare(lb.row);
      return a.name.localeCompare(b.name);
    });

    // Group by zone
    const groups: Record<string, PickListItem[]> = {};
    filtered.forEach((item) => {
      const z = item._loc ? item._loc.zone : "Unassigned";
      if (!groups[z]) groups[z] = [];
      groups[z].push(item);
    });

    return groups;
  }, [itemsForMode, filterQuery]);

  const totalItemsCount = useMemo(() => {
    return Object.values(groupedZones).reduce((acc, list) => acc + list.length, 0);
  }, [groupedZones]);

  // Known zones in walking order, then any zone that parsed but is missing from
  // SHELF_PICK_ORDER, then the unplaced group. Without the middle term a zone
  // added to SHELF_ZONES and forgotten in SHELF_PICK_ORDER would render no card
  // at all while its bottles still counted toward the total — items silently
  // missing from a restock run. The coverage test should catch that first.
  const orderedZones = useMemo(() => [
    ...SHELF_PICK_ORDER,
    ...Object.keys(groupedZones)
      .filter(zone => zone !== "Unassigned" && !SHELF_PICK_ORDER.includes(zone))
      .sort(),
    "Unassigned",
  ], [groupedZones]);

  return (
    <div style={styles.container}>
      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScanBarcode}
        title="Scan Bottle to Mark Picked / Checked"
      />

      {/* Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Floor Restock & Pick List</h1>
          <p style={styles.subtitle}>
            Optimized bottle pull sheets sorted by walking path across physical storage zones.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <KioskControls />
          <button
            type="button"
            style={{
              background: "rgba(249,175,54,0.15)",
              border: "1px solid var(--gold, #F9AF36)",
              color: "var(--gold, #F9AF36)",
              borderRadius: 8,
              padding: "8px 14px",
              fontWeight: 700,
              cursor: "pointer",
            }}
            onClick={() => setScannerOpen(true)}
            title="Scan barcode to check off item"
          >
            📷 Scan to Pick
          </button>
          <button style={styles.btnPrint} onClick={loadData} title="Reload fresh data">
            ↻ Sync
          </button>
          <button style={styles.btnPrint} onClick={() => window.print()}>
            🖨️ Print Pick List
          </button>
          {Object.keys(checkedIds).length > 0 && (
            <button
              type="button"
              onClick={clearAllChecks}
              style={{
                background: "transparent",
                border: "1px solid var(--border, #27272a)",
                color: "var(--muted, #a1a1aa)",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Clear Checked ({Object.values(checkedIds).filter(Boolean).length})
            </button>
          )}
        </div>
      </header>

      {isOfflineCached && (
        <div style={styles.offlineBanner}>
          📶 <strong>Offline Cellar Mode:</strong> Displaying cached pick list. Checkmarks are saved locally and will persist until cleared.
        </div>
      )}

      {/* Mode Selector & Par Level Filter */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div style={styles.modesRow}>
          <button
            style={{
              ...styles.modeBtn,
              ...(mode === "restock" ? styles.modeBtnActive : {}),
            }}
            onClick={() => setMode("restock")}
          >
            Low Stock Restock
          </button>
          <button
            style={{
              ...styles.modeBtn,
              ...(mode === "unassigned" ? styles.modeBtnActive : {}),
            }}
            onClick={() => setMode("unassigned")}
          >
            Unassigned Bottles
          </button>
          <button
            style={{
              ...styles.modeBtn,
              ...(mode === "custom" ? styles.modeBtnActive : {}),
            }}
            onClick={() => setMode("custom")}
          >
            Custom Pull List ({customIds.length})
          </button>
        </div>

        {mode === "restock" && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted)" }}>
            <span style={{ fontWeight: 700 }}>Par Level:</span>
            {[
              { val: 0, label: "Out of Stock (0)" },
              { val: 1, label: "Par ≤ 1" },
              { val: 2, label: "Par ≤ 2" },
              { val: 3, label: "Par ≤ 3" },
              { val: 5, label: "Par ≤ 5" },
            ].map((p) => (
              <button
                key={p.val}
                type="button"
                onClick={() => setParThreshold(p.val)}
                style={{
                  background: parThreshold === p.val ? "rgba(249,175,54,0.15)" : "transparent",
                  border: parThreshold === p.val ? "1px solid var(--gold, #F9AF36)" : "1px solid var(--border, #27272a)",
                  color: parThreshold === p.val ? "var(--gold, #F9AF36)" : "var(--muted, #a1a1aa)",
                  borderRadius: 6,
                  padding: "4px 8px",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Custom Mode Search Dropdown */}
      {mode === "custom" && (
        <div style={styles.customSearchWrap}>
          <input
            type="text"
            placeholder="Search inventory to add bottles to custom pick list..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchResults.length > 0 && (
            <div style={styles.searchDropdown}>
              {searchResults.map((b) => (
                <div
                  key={b.id}
                  style={styles.searchResultItem}
                  onClick={() => addCustomBottle(b)}
                >
                  <div>
                    <strong>{b.name}</strong>
                    <div style={styles.searchResultSub}>
                      {b.shelf_location?.join(", ") || "No shelf location"} · Count: {b.inventory_count ?? "—"}
                    </div>
                  </div>
                  <span style={styles.plusBtn}>+ Add</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* In-list filter */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Filter displayed bottles..."
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          style={styles.filterInput}
        />
        <div style={styles.statsTag}>
          {totalItemsCount} total pulls · {Object.keys(groupedZones).length} zones
        </div>
      </div>

      {/* Pick List Content */}
      {loading ? (
        <div style={styles.loader}>Loading shelf layout…</div>
      ) : totalItemsCount === 0 ? (
        <div style={styles.emptyState}>
          {mode === "restock"
            ? "No low-stock items requiring restock at this time."
            : mode === "unassigned"
            ? "All bottles have assigned shelf locations."
            : "No items in custom pick list. Search bottles above to add."}
        </div>
      ) : (
        <div style={styles.zonesList}>
          {orderedZones.map((zone) => {
            const zoneItems = groupedZones[zone];
            if (!zoneItems || zoneItems.length === 0) return null;
            const zoneLabel = shelfZoneLabel(zone);

            return (
              <div key={zone} style={styles.zoneCard}>
                <div style={styles.zoneHeader}>
                  <span style={styles.zoneTitle}>{zoneLabel}</span>
                  <span style={styles.zoneCount}>({zoneItems.length} items)</span>
                </div>

                <div style={styles.itemsTable}>
                  {zoneItems.map((item) => {
                    const key = `${item.id}_${item._code}`;
                    const isChecked = !!checkedIds[key];

                    return (
                      <div
                        key={key}
                        tabIndex={0}
                        role="button"
                        aria-pressed={isChecked}
                        style={{
                          ...styles.itemRow,
                          ...(isPhone ? { flexWrap: "wrap" } : {}),
                          opacity: isChecked ? 0.45 : 1,
                          textDecoration: isChecked ? "line-through" : "none",
                        }}
                        onClick={() => toggleCheck(key)}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            toggleCheck(key);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          readOnly
                          tabIndex={-1}
                          style={styles.checkbox}
                          aria-hidden="true"
                        />
                        <div style={styles.locBadge}>{item._code}</div>
                        <div style={isPhone ? { ...styles.itemInfo, flex: "1 1 150px" } : styles.itemInfo}>
                          <div style={styles.itemName}>{item.name}</div>
                          <div style={styles.itemCategory}>{item.category_slug || "Spirit"}</div>
                        </div>
                        <div style={isPhone ? { ...styles.countBadge, marginLeft: "auto" } : styles.countBadge}>
                          Count: {item.inventory_count ?? "0"}
                          {item.reorder_point != null && (
                            <span style={styles.minPar}> / Min: {item.reorder_point}</span>
                          )}
                        </div>
                        {mode === "custom" && (
                          <button
                            type="button"
                            style={styles.removeBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCustomBottle(item.id);
                            }}
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 20px 60px",
    maxWidth: 1000,
    margin: "0 auto",
    color: "var(--text, #F7F5F0)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    gap: 16,
    flexWrap: "wrap",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  btnPrint: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    padding: "8px 18px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  modesRow: {
    display: "flex",
    gap: 8,
    borderBottom: "1px solid var(--border, #231D19)",
    marginBottom: 16,
  },
  // minWidth "min-content": global.css gives phone buttons min-width 44px, which replaces a flex
  // item's content-size floor, so this wrapping label shrank past its own text (4d verifier).
  modeBtn: {
    background: "transparent",
    border: 0,
    borderBottom: "2px solid transparent",
    color: "var(--muted, #A39D98)",
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    minWidth: "min-content",
  },
  modeBtnActive: {
    color: "var(--gold, #F9AF36)",
    borderColor: "var(--gold, #F9AF36)",
  },
  customSearchWrap: {
    position: "relative",
    marginBottom: 16,
  },
  searchInput: {
    width: "100%",
    padding: "10px 14px",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--gold, #F9AF36)",
    borderRadius: 8,
    color: "var(--text, #F7F5F0)",
    fontSize: 14,
    outline: "none",
  },
  searchDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: "0 0 8px 8px",
    maxHeight: 250,
    overflowY: "auto",
    zIndex: 50,
    boxShadow: "0 10px 30px rgba(0,0,0,0.8)",
  },
  searchResultItem: {
    padding: "10px 14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid var(--border, #231D19)",
    cursor: "pointer",
  },
  searchResultSub: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
    marginTop: 2,
  },
  plusBtn: {
    fontSize: 12,
    color: "var(--gold, #F9AF36)",
    fontWeight: 700,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    gap: 12,
  },
  filterInput: {
    flex: 1,
    padding: "8px 12px",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    outline: "none",
  },
  statsTag: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
    whiteSpace: "nowrap",
  },
  zonesList: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  zoneCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 10,
    overflow: "hidden",
  },
  zoneHeader: {
    padding: "10px 16px",
    background: "rgba(249, 175, 54, 0.05)",
    borderBottom: "1px solid var(--border, #231D19)",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  zoneTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 16,
    fontWeight: "bold",
    color: "var(--gold, #F9AF36)",
  },
  zoneCount: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
  },
  itemsTable: {
    display: "flex",
    flexDirection: "column",
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 16px",
    borderBottom: "1px solid var(--border, #231D19)",
    gap: 12,
    cursor: "pointer",
    userSelect: "none",
    transition: "background 0.1s ease",
  },
  checkbox: {
    width: 18,
    height: 18,
    accentColor: "var(--gold, #F9AF36)",
    cursor: "pointer",
  },
  locBadge: {
    background: "var(--bg, #0C0907)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    color: "var(--gold, #F9AF36)",
    padding: "4px 8px",
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: 12,
    fontWeight: 700,
    minWidth: 70,
    textAlign: "center",
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    overflowWrap: "anywhere",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--text, #F7F5F0)",
  },
  itemCategory: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
  },
  countBadge: {
    fontSize: 12,
    color: "var(--text, #F7F5F0)",
    fontFamily: "monospace",
  },
  minPar: {
    color: "var(--muted, #A39D98)",
  },
  removeBtn: {
    background: "transparent",
    border: 0,
    color: "var(--red, #FF8A7A)",
    fontSize: 18,
    cursor: "pointer",
    padding: "0 4px",
    lineHeight: 1,
  },
  loader: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #A39D98)",
  },
  emptyState: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #A39D98)",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 10,
    fontSize: 14,
  },
  offlineBanner: {
    padding: "10px 14px",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 8,
    color: "var(--gold, #F9AF36)",
    fontSize: 12,
    marginBottom: 16,
  },
};
